import type { BadgeProps } from '@/ui';

/**
 * Ported from the legacy `Enums.TicketTypes` / `Enums.TicketStatuses`. The numeric
 * values are the wire format and must not be renumbered.
 */
export const TicketType = {
  Single: 0,
  Combi: 1,
  System: 2,
} as const;

export type TicketType = (typeof TicketType)[keyof typeof TicketType];

export const TicketStatus = {
  Open: 0,
  Won: 1,
  Lost: 2,
  Cancelled: 3,
  Sold: 4,
  Saved: 5,
  Booked: 6,
} as const;

export type TicketStatus = (typeof TicketStatus)[keyof typeof TicketStatus];

/**
 * The legacy mapping labelled 5 and 6 both as "Saved"; they are separated here
 * because a booked ticket behaves differently in the UI to a saved one.
 */
export const TICKET_STATUS_LABEL: Record<TicketStatus, string> = {
  [TicketStatus.Open]: 'Open',
  [TicketStatus.Won]: 'Won',
  [TicketStatus.Lost]: 'Lost',
  [TicketStatus.Cancelled]: 'Cancelled',
  [TicketStatus.Sold]: 'Sold',
  [TicketStatus.Saved]: 'Saved',
  [TicketStatus.Booked]: 'Booked',
};

type Tone = NonNullable<BadgeProps['tone']>;

/**
 * Figma Badge tones for MyBetTicketCard StatusBadge:
 * Won → Success, Lost → Danger, Open/Sold → Info, others → Neutral.
 */
export const TICKET_STATUS_TONE: Record<TicketStatus, Tone> = {
  [TicketStatus.Open]: 'info',
  [TicketStatus.Won]: 'success',
  [TicketStatus.Lost]: 'danger',
  [TicketStatus.Cancelled]: 'neutral',
  [TicketStatus.Sold]: 'info',
  [TicketStatus.Saved]: 'neutral',
  [TicketStatus.Booked]: 'neutral',
};

export const BetType = {
  Match: 0,
  Outright: 1,
} as const;

export type BetType = (typeof BetType)[keyof typeof BetType];

/** My bets status pills — maps to `TicketFilter` boolean flags server-side. */
export type MyBetsStatusTab = 'open' | 'live' | 'settled' | 'saved';

/**
 * Settled-tab sub-filters (legacy mobile Settled toggles).
 * Status flags are OR’d server-side; `bonus` is an AND on `UseBonusCredits`.
 */
export interface SettledTicketSubFilters {
  won: boolean;
  lost: boolean;
  sold: boolean;
  cancelled: boolean;
  bonus: boolean;
}

export const DEFAULT_SETTLED_SUB_FILTERS: SettledTicketSubFilters = {
  won: true,
  lost: true,
  sold: true,
  cancelled: true,
  bonus: false,
};

/** Selection settlement for status dots / expanded rows (`bet.Won`). */
export type BetSettlement = 'open' | 'won' | 'lost';

/** Front `BetItem` from Users API ticket list/details. */
export interface TicketBetItem {
  Live?: boolean;
  InPlay?: boolean;
  Date?: string | null;
  Time?: string | null;
  Sport?: string | null;
  Category?: string | null;
  Tournament?: string | null;
  Home?: string | null;
  Away?: string | null;
  MarketId?: string | null;
  Market?: string | null;
  Specifier?: string | null;
  SpecifierDescription?: string | null;
  OutcomeId?: string | null;
  Outcome?: string | null;
  OutcomeName?: string | null;
  MatchId?: number;
  BetScore?: string | null;
  Score?: string | null;
  Value?: number;
  Void?: number | null;
  Banked?: boolean;
  Won?: boolean | null;
  SportId?: number | null;
  TournamentId?: number | null;
  Type?: number;
  IsCustom?: boolean;
  Selections?: TicketBetItem[] | null;
}

/** UsersAPI `PartialCashoutHistoryItem` on ticket list / details. */
export interface PartialCashoutHistoryItem {
  Id?: number;
  Stake: number;
  Win: number;
  Date: string;
}

/** Front `TicketItem` from Users API ticket list. */
export interface TicketListItem {
  Id: string;
  Date: string;
  Type: TicketType | number;
  SystemMax?: number;
  SystemMin?: number;
  Stake: number;
  Odds: number;
  Combinations?: number;
  PossibleWin: number;
  Win?: number | null;
  Status: TicketStatus | number;
  Timestamp?: string;
  WinTimestamp?: string | null;
  Bets?: TicketBetItem[] | null;
  BetsCount?: number | null;
  TicketGuid?: string | null;
  AllowCashout?: boolean;
  InitialStake?: number;
  InitialPossibleWin?: number;
  CashoutWin?: number | null;
  /** Prior partial cashouts from UsersAPI (list includes these when any exist). */
  PartialCashoutHistory?: PartialCashoutHistoryItem[] | null;
  /** Placed ticket probability (1 / product of placed odds) — required for cashout offer calc. */
  Probability?: number | null;
  Live?: boolean;
  DepositBonus?: boolean;
  HasRewards?: boolean;
  Bonuses?: { Type?: number; Name?: string; Bonus?: number }[] | null;
  /** True when this open ticket is already used as a KreditBet creditor. */
  Creditor?: boolean;
  /** Linked main ticket number when this ticket is a KreditBet. */
  CreditorNumber?: string | null;
  /** Settlement status of the KreditBet link (`TicketStatuses`). */
  CreditorStatus?: number | null;
}

/** `TicketFilter` flags sent as `TableFilters` on list. */
export interface TicketTableFilters {
  MachineGuid: string;
  Bets: boolean;
  Live: boolean;
  Open: boolean;
  Won: boolean;
  Lost: boolean;
  Sold: boolean;
  Cancelled: boolean;
  Saved: boolean;
  Bonus: boolean;
  DateFrom?: string | null;
}

export interface TicketListPaging {
  PageNumber: number;
  NumberOfResultsToDisplay: number;
}

export interface TicketListSorting {
  SortBy: string;
  Direction: 'asc' | 'desc';
}

export interface TicketListRequest {
  TableFilters: TicketTableFilters;
  Paging: TicketListPaging;
  Sorting: TicketListSorting;
}

export interface TicketListPagingInfo {
  PageNumber: number;
  NumberOfPages: number;
  NumberOfResults: number;
  NumberOfResultsToDisplay?: number;
  TotalNumberOfResults?: number;
}

export interface TicketListResponse {
  Data: TicketListItem[];
  Filter: TicketListPagingInfo;
}

export interface TicketCountRequest {
  Value: string;
  DateFrom?: string | null;
}

export interface TicketCountResponse {
  LiveTickets: number;
  OpenTickets: number;
}

export const TICKETS_PAGE_SIZE = 10;

/** A ticket is settled once it can no longer change, which gates cashout and reuse. */
export function isTicketSettled(status: TicketStatus): boolean {
  return (
    status === TicketStatus.Won || status === TicketStatus.Lost || status === TicketStatus.Cancelled
  );
}

/** Won / cashed-out tickets show realized win instead of possible win. */
export function ticketShowsRealizedWin(status: TicketStatus | number): boolean {
  return status === TicketStatus.Won || status === TicketStatus.Sold;
}

export function canReuseTicket(status: TicketStatus | number): boolean {
  return status === TicketStatus.Open;
}

export function betSettlement(won: boolean | null | undefined): BetSettlement {
  if (won === true) return 'won';
  if (won === false) return 'lost';
  return 'open';
}

/** Legacy type title: `Single` / `{n} x Combi` / `System (min/max)`. */
export function ticketTypeLabel(ticket: TicketListItem): string {
  const type = ticket.Type;
  const bets = ticket.Bets?.length ?? ticket.BetsCount ?? 0;
  if (type === TicketType.Single) return 'Single';
  if (type === TicketType.System) {
    const min = ticket.SystemMin ?? 0;
    const max = ticket.SystemMax ?? bets;
    return `System (${min}/${max})`;
  }
  return `${bets || 1} x Combi`;
}

/** Empty filter flags; callers set the active tab bits. */
export function emptyTicketTableFilters(
  machineGuid: string,
  dateFrom?: string | null,
): TicketTableFilters {
  return {
    MachineGuid: machineGuid,
    Bets: true,
    Live: false,
    Open: false,
    Won: false,
    Lost: false,
    Sold: false,
    Cancelled: false,
    Saved: false,
    Bonus: false,
    DateFrom: dateFrom ?? null,
  };
}

/** Port of legacy `FilterOpenTickets` / `FilterLiveTickets` / etc. */
export function ticketFiltersForStatusTab(
  tab: MyBetsStatusTab,
  machineGuid: string,
  dateFrom?: string | null,
  settledFilters: SettledTicketSubFilters = DEFAULT_SETTLED_SUB_FILTERS,
): TicketTableFilters {
  const base = emptyTicketTableFilters(machineGuid, dateFrom);
  switch (tab) {
    case 'open':
      return { ...base, Open: true, Live: true };
    case 'live':
      return { ...base, Live: true, DateFrom: null };
    case 'settled':
      return {
        ...base,
        Won: settledFilters.won,
        Lost: settledFilters.lost,
        Cancelled: settledFilters.cancelled,
        Sold: settledFilters.sold,
        Bonus: settledFilters.bonus,
      };
    case 'saved':
      return { ...base, Saved: true };
  }
}

export const MY_BETS_DATE_PRESETS = [
  { days: 0, labelKey: 'Today', defaultLabel: 'Today' },
  { days: 3, labelKey: 'Last3Days', defaultLabel: 'Last 3 days' },
  { days: 7, labelKey: 'Last7Days', defaultLabel: 'Last 7 days' },
  { days: 14, labelKey: 'Last14Days', defaultLabel: 'Last 14 days' },
  { days: 30, labelKey: 'LastMonth', defaultLabel: 'Last month' },
  { days: 90, labelKey: 'Last3Months', defaultLabel: 'Last 3 months' },
  { days: 180, labelKey: 'Last6Months', defaultLabel: 'Last 6 months' },
] as const;

export type MyBetsDatePresetDays = (typeof MY_BETS_DATE_PRESETS)[number]['days'];

/** `DateFrom` for a preset: now minus `days` (0 = start of today local). */
export function myBetsDateFrom(days: MyBetsDatePresetDays, now = new Date()): string {
  if (days === 0) {
    const start = new Date(now);
    start.setHours(0, 0, 0, 0);
    return start.toISOString();
  }
  const from = new Date(now.getTime() - days * 24 * 60 * 60 * 1000);
  return from.toISOString();
}
